"use client"

import * as React from "react"
import * as AccordionPrimitive from "@radix-ui/react-accordion"
import { ChevronDown } from "lucide-react"

import { cn } from "../utils"
import { Badge } from "./badge"

const Accordion = AccordionPrimitive.Root

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item ref={ref} className={cn("border-b", className)} {...props} />
))
AccordionItem.displayName = "AccordionItem"

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> & {
    count?: React.ReactNode
    headerClassName?: string
    nonButton?: boolean
  }
>(({ className, children, count, headerClassName, nonButton = false, ...props }, ref) => {
  const triggerClassName = cn(
    "hover:bg-tinted flex items-center gap-2 rounded-lg py-1 pr-3 pl-2! text-left text-sm font-medium transition-all [&[data-state=open]>svg]:rotate-180",
    nonButton && "cursor-pointer data-[disabled]:cursor-not-allowed focus-visible:outline-hidden focus-visible:ring-1",
    className
  )

  return (
    <AccordionPrimitive.Header className={cn("flex", headerClassName)}>
      {nonButton ? (
        <AccordionPrimitive.Trigger ref={ref} asChild {...props}>
          <div
            role="button"
            tabIndex={0}
            className={triggerClassName}
            onKeyDown={(event) => {
              if (event.defaultPrevented) return
              if (event.key !== "Enter" && event.key !== " ") return

              const isDisabled =
                event.currentTarget.getAttribute("data-disabled") !== null ||
                event.currentTarget.getAttribute("aria-disabled") === "true"
              if (isDisabled) return

              event.preventDefault()
              event.currentTarget.click()
            }}
          >
            <ChevronDown className="text-muted-foreground h-4 w-4 shrink-0 transition-transform duration-200" />
            {children}
            {count !== undefined && (
              <Badge variant="secondary" size="xs">
                {count}
              </Badge>
            )}
          </div>
        </AccordionPrimitive.Trigger>
      ) : (
        <AccordionPrimitive.Trigger ref={ref} className={triggerClassName} {...props}>
          <ChevronDown className="text-muted-foreground h-4 w-4 shrink-0 transition-transform duration-200" />
          {children}
          {count !== undefined && (
            <Badge variant="secondary" size="xs">
              {count}
            </Badge>
          )}
        </AccordionPrimitive.Trigger>
      )}
    </AccordionPrimitive.Header>
  )
})
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden text-sm"
    {...props}
  >
    <div className={cn("pt-0 pb-4", className)}>{children}</div>
  </AccordionPrimitive.Content>
))
AccordionContent.displayName = AccordionPrimitive.Content.displayName

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent }
